import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowCounterClockwise, Brain, CheckCircle, DeviceMobile, DownloadSimple, Gauge, ShieldCheck, SlidersHorizontal } from '@phosphor-icons/react'
import { apiRequest, errorMessage, jsonBody } from '../api/client'
import type { ActivePlan, AiRuntimeProfileHistory, AiRuntimeProfileMutation } from '../api/types'
import { getPwaInstallState, manualPwaInstallMessage, requestPwaInstall, type PwaInstallState } from '../app/pwa'
import { queryClient } from '../app/queryClient'
import { ErrorState, PageLoader } from '../components/States'

type MutationView = { stateVersion: number }

function coachStatus(plan: ActivePlan): string {
  if (!plan.aiCoachEnabled || plan.coachRolloutMode === 'Off') return 'Coach je vypnutý'
  if (plan.coachRolloutMode === 'Shadow') return 'Coach se učí bez zásahů'
  if (plan.coachRolloutMode === 'Suggest') return 'Coach je aktivní'
  return 'Coach upravuje automaticky'
}

export function SettingsPage() {
  const active = useQuery({ queryKey: ['active-plan'], queryFn: () => apiRequest<ActivePlan>('/plans/active') })
  const runtime = useQuery({
    queryKey: ['ai-runtime-profile'],
    queryFn: () => apiRequest<AiRuntimeProfileHistory>('/coach/runtime-profile'),
    retry: false,
  })
  const [pain, setPain] = useState('none')
  const [equipment, setEquipment] = useState('hrazda, bradla, odporové gumy')
  const [timeBudget, setTimeBudget] = useState('60')
  const [rollbackConfirmed, setRollbackConfirmed] = useState(false)
  const [installState, setInstallState] = useState<PwaInstallState>(() => getPwaInstallState())
  const [installMessage, setInstallMessage] = useState<string | null>(null)

  useEffect(() => {
    const updateInstallState = () => setInstallState(getPwaInstallState())
    window.addEventListener('repinu:install-state', updateInstallState)
    return () => window.removeEventListener('repinu:install-state', updateInstallState)
  }, [])

  const calibration = useMutation({
    mutationFn: async () => {
      const current = active.data?.calibration
      if (!current) throw new Error('Aktivní plán nemá otevřenou kalibraci.')
      let version = current.stateVersion
      const answers: Array<[string, string]> = [
        ['current_pain', pain],
        ['available_equipment', equipment.trim()],
        ['weekly_time_budget', timeBudget],
      ]
      for (const [code, answer] of answers) {
        const result = await apiRequest<MutationView>(`/calibrations/${current.id}/answers/${code}`, {
          method: 'PUT',
          body: jsonBody({ answer, expectedStateVersion: version }),
        })
        version = result.stateVersion
      }
      return apiRequest<MutationView>(`/calibrations/${current.id}/complete`, {
        method: 'POST',
        body: jsonBody({ expectedStateVersion: version }),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['active-plan'] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })

  const rollback = useMutation({
    mutationFn: async () => {
      const state = runtime.data
      if (!state?.previous) throw new Error('Předchozí nastavení AI není dostupné.')
      return apiRequest<AiRuntimeProfileMutation>('/coach/runtime-profile/rollback', {
        method: 'POST',
        body: jsonBody({
          currentProfileId: state.current.id,
          expectedProfileStateVersion: state.current.stateVersion,
          expectedActivationStateVersion: state.activationStateVersion,
          explicitUserConfirmation: rollbackConfirmed,
        }),
      })
    },
    onSuccess: async () => {
      setRollbackConfirmed(false)
      await queryClient.invalidateQueries({ queryKey: ['ai-runtime-profile'] })
      await queryClient.invalidateQueries({ queryKey: ['active-plan'] })
    },
  })

  async function installApp() {
    setInstallMessage(null)
    if (installState === 'manual') {
      setInstallMessage(manualPwaInstallMessage())
      return
    }

    const outcome = await requestPwaInstall()
    setInstallState(getPwaInstallState())
    if (outcome === 'dismissed') {
      setInstallMessage('Instalace nebyla dokončena. Můžeš se sem vrátit později.')
    } else if (outcome === 'unavailable') {
      setInstallMessage(manualPwaInstallMessage())
    } else if (outcome === 'accepted') {
      setInstallMessage('RepInu se instaluje do zařízení.')
    }
  }

  if (active.isPending) return <PageLoader label="Načítám nastavení…" />
  if (active.isError) return <ErrorState error={active.error} onRetry={() => active.refetch()} />
  const plan = active.data

  return (
    <div className="page-stack settings-page">
      <div className="screen-kicker"><span>Nastavení</span><span>RepInu</span></div>
      <header className="settings-hero">
        <div><p className="eyebrow">Tvoje aplikace</p><h1>Nastavení</h1><p>Coach, bezpečnost, instalace a používaný tréninkový plán.</p></div>
      </header>

      <section className="content-section settings-section">
        <div className="settings-icon"><Brain size={24} weight="duotone" /></div>
        <div className="settings-copy">
          <h2>AI coach</h2>
          <p>Návrhy vycházejí z tvého plánu a uložených výkonů. Změny čekají na potvrzení a zápis tréninku funguje i bez AI.</p>
        </div>
        <span className="status-pill"><CheckCircle size={17} weight="fill" /> {coachStatus(plan)}</span>
      </section>

      <section className="content-section settings-section">
        <div className="settings-icon"><ShieldCheck size={24} weight="duotone" /></div>
        <div className="settings-copy">
          <h2>Bezpečnost rozhodnutí</h2>
          <p>Bolest a safety stop mají vždy přednost. Větší změny plánu se neprovedou bez tvého potvrzení.</p>
        </div>
        <span className="status-pill"><CheckCircle size={17} weight="fill" /> Potvrzení zapnuto</span>
      </section>

      <section className="content-section settings-section" aria-labelledby="install-repinu-title">
        <div className="settings-icon"><DeviceMobile size={24} weight="duotone" /></div>
        <div className="settings-copy">
          <h2 id="install-repinu-title">RepInu v telefonu</h2>
          <p>{installState === 'installed'
            ? 'Aplikace je nainstalovaná. Aktualizace nabídne automaticky při příštím online otevření.'
            : 'Nainstaluj si stejnou RepInu na plochu telefonu. Účet i tréninková data zůstávají společné.'}</p>
          {installMessage && <p className="install-help" role="status">{installMessage}</p>}
        </div>
        {installState === 'installed'
          ? <span className="status-pill"><CheckCircle size={17} weight="fill" /> Nainstalováno</span>
          : (
            <button type="button" className="button button--quiet" onClick={installApp}>
              <DownloadSimple size={18} /> {installState === 'available' ? 'Nainstalovat' : 'Jak nainstalovat'}
            </button>
          )}
      </section>

      {runtime.data?.previous && (
        <section className="content-section settings-section">
          <div className="settings-icon"><ArrowCounterClockwise size={24} weight="duotone" /></div>
          <div className="settings-copy">
            <h2>Obnova AI coache</h2>
            <p>Pokud by se nová doporučení nechovala správně, můžeš obnovit poslední ověřené nastavení. Plán ani historie se nezmění.</p>
            <label className="settings-confirmation">
              <input type="checkbox" checked={rollbackConfirmed} onChange={(event) => setRollbackConfirmed(event.target.checked)} />
              <span>Rozumím a chci obnovit předchozí ověřené nastavení AI.</span>
            </label>
            {rollback.isError && <p className="inline-error">{errorMessage(rollback.error)}</p>}
            {rollback.isSuccess && <p className="inline-success">Předchozí nastavení AI je aktivní.</p>}
          </div>
          <button type="button" className="button button--quiet" onClick={() => rollback.mutate()} disabled={!rollbackConfirmed || rollback.isPending}>
            <ArrowCounterClockwise size={18} /> {rollback.isPending ? 'Obnovuji…' : 'Obnovit AI'}
          </button>
        </section>
      )}

      {plan.calibration && plan.calibration.status !== 'Completed' && (
        <section className="content-section calibration-form">
          <div className="section-heading">
            <div><p className="eyebrow">Adaptivní start</p><h2>Krátké doladění</h2><p>Historii už máme. Potřebujeme jen informace, které z ní nelze bezpečně odvodit.</p></div>
            <Gauge size={28} weight="duotone" />
          </div>
          <div className="form-grid">
            <label><span>Aktuální bolest</span><select value={pain} onChange={(event) => setPain(event.target.value)}><option value="none">Bez bolesti</option><option value="mild">Mírná citlivost, cvičit opatrně</option><option value="active">Aktivní bolest, upravit plán</option></select></label>
            <label><span>Dostupné vybavení</span><input value={equipment} onChange={(event) => setEquipment(event.target.value)} maxLength={500} /></label>
            <label><span>Čas na jeden trénink v minutách</span><input type="number" min="15" max="180" value={timeBudget} onChange={(event) => setTimeBudget(event.target.value)} /></label>
          </div>
          <button type="button" className="button button--primary" onClick={() => calibration.mutate()} disabled={calibration.isPending || !equipment.trim()}>{calibration.isPending ? 'Ukládám…' : 'Uložit a dokončit kalibraci'}</button>
          {calibration.isError && <p className="inline-error">{errorMessage(calibration.error)}</p>}
        </section>
      )}

      {(!plan.calibration || plan.calibration.status === 'Completed') && (
        <section className="notice-band notice-band--success">
          <CheckCircle size={24} weight="fill" />
          <div><strong>Coach má potřebný kontext</strong><p>Pracuje s historií a uloženými kalibračními údaji.</p></div>
        </section>
      )}

      <section className="content-section settings-section">
        <div className="settings-icon"><SlidersHorizontal size={24} weight="duotone" /></div>
        <div className="settings-copy"><h2>Používaný plán</h2><p>{plan.name}</p></div>
        <span className="status-pill"><CheckCircle size={17} weight="fill" /> Aktivní</span>
      </section>
    </div>
  )
}